import React, { useEffect } from 'react';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useHistory } from 'react-router-dom';
import { MutationStatus } from 'react-query';
import SpecialConsiderationGetStarted from './schema/SpecialConsiderationGetStarted';
import ControlledAutocomplete from '../../../components/form-items/ControlledAutocomplete';
import {
  ExamSeriesModel,
  useGetExamSeries,
} from '../../../api-refactored/special-consideration/useGetExamSeries';
import {
  ExamLevelModel,
  usePostExamLevels,
} from '../../../api-refactored/special-consideration/usePostExamLevels';
import RouteKey from '../../../routes/key';
import {
  ExamLevelSeriesRequest,
  SpecialConsiderationEligibilityResponse,
  usePostCheckEligibility,
} from '../../../api-refactored/special-consideration/usePostCheckEligibility';
import { ScInstructionInfo } from '../CoordinatorListing/SelectExamLevelCoordinatorModal';

const { specialConsideration: specialConsiderationRoute } = RouteKey.child;
const { specialConsideration, instructions } = RouteKey['special-consideration'].child;

const useStyles = makeStyles()((theme) =>
({
  customClass: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  controlledAutoComplete: {
    paddingTop: theme.spacing(3),
  },
  errorText: {
    marginTop: theme.spacing(3),
    color: theme.color.state.stateError,
  },
}));

interface SelectExamLevelModalProps {
  modalState: boolean;
  onDialogClose: () => void;
  examLevel: string | null;
  examSeries: string | null;
  isError: boolean;
  dialogCustomStatus: MutationStatus;
  setExamLevel: React.Dispatch<React.SetStateAction<string | null>>;
  setExamSeries: React.Dispatch<React.SetStateAction<string | null>>;
  setIsError: React.Dispatch<React.SetStateAction<boolean>>;
  setDialogCustomStatus: React.Dispatch<React.SetStateAction<MutationStatus>>;
}

const SelectExamLevelModal = ({
  modalState,
  onDialogClose,
  examLevel,
  examSeries,
  isError,
  dialogCustomStatus,
  setExamLevel,
  setExamSeries,
  setIsError,
  setDialogCustomStatus,
}: SelectExamLevelModalProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();

  const formHook = useForm<ExamLevelSeriesRequest>({
    mode: 'onSubmit',
    resolver: joiResolver(SpecialConsiderationGetStarted()),
  });

  const { control } = formHook;

  const { data: examLevelData, mutate: mutatePostExamLevels } = usePostExamLevels();

  const { data: examSeriesData } = useGetExamSeries();

  const {
    data: checkEligibilityData,
    mutate: mutatePostCheckEligibility,
  } = usePostCheckEligibility({
    onSuccess: (data: SpecialConsiderationEligibilityResponse) => {
      if (!data.eligible) {
        setIsError(true);
        setDialogCustomStatus('error');
      } else if (examLevel && examSeries) {
        const scInstructionInfo: ScInstructionInfo = {
          examLevelUuid: examLevel,
          examSeriesUuid: examSeries,
          isContinueApplication: false,
        };
        setDialogCustomStatus('success');
        history.push(
          `/${specialConsiderationRoute}/${specialConsideration.route}/${instructions.route}`,
          scInstructionInfo
        );
      }
    },
  });

  useEffect(
    () =>
      mutatePostExamLevels({
        codeType: 'APPL_EXAM_LVL_CD',
        codeInList: ['GCEA', 'GCEO', 'GCEN'],
      }),
    [mutatePostExamLevels]
  );

  return (
    <Dialog open={modalState} onClose={onDialogClose} maxWidth="md">
      <DialogTitle>{t('specialConsideration->>SelectExamLevelModal.GetStarted')}</DialogTitle>
      <DialogContent>
        <>{t('specialConsideration->>SelectExamLevelModal.Content')}</>
        <Box className={classes.controlledAutoComplete}>
          {examLevelData && (
            <ControlledAutocomplete<ExamLevelModel, false, false, undefined>
              label={t('specialConsideration->>SelectExamLevelModal.ExamLevel')}
              control={control}
              name="examLevel"
              id="input-select-special-consideration-exam-level"
              error={isError && examLevel != null && examSeries != null}
              options={examLevelData}
              getOptionLabel={(option: ExamLevelModel) => option.description}
              placeholder={t('specialConsideration->>SelectExamLevelModal.SelectExamLevel')}
              transform={{
                input: (selectedOption, options) =>
                  options.find((option) => option.code === selectedOption) ?? null,
                output: (selectedOption) => {
                  const selectedExamLevel = selectedOption ? selectedOption.code : null;
                  setExamLevel(selectedExamLevel);
                  return selectedExamLevel;
                },
              }}
            />
          )}
        </Box>
        <Box className={classes.controlledAutoComplete}>
          {examSeriesData && (
            <ControlledAutocomplete<ExamSeriesModel, false, false, undefined>
              label={t('specialConsideration->>SelectExamLevelModal.ExamSeries')}
              control={control}
              name="examSeries"
              id="input-select-special-consideration-exam-series"
              error={isError && examLevel != null && examSeries != null}
              options={examSeriesData}
              getOptionLabel={(option: ExamSeriesModel) => option.description}
              placeholder={t('specialConsideration->>SelectExamLevelModal.SelectExamSeries')}
              transform={{
                input: (selectedOption, options) =>
                  options.find((option) => option.code === selectedOption) ?? null,
                output: (selectedOption) => {
                  const selectedExamSeries = selectedOption ? selectedOption.code : null;
                  setExamSeries(selectedExamSeries);
                  return selectedExamSeries;
                },
              }}
            />
          )}
        </Box>
        {isError && examLevel != null && examSeries != null && (
          <Typography className={classes.errorText}>
            {checkEligibilityData?.error.message}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Box className={classes.buttonContainer}>
          <Button onClick={onDialogClose} variant="text">
            {t('specialConsideration->>SelectExamLevelModal.Cancel')}
          </Button>
          <ApiButton
            id="button-proceed-sc"
            disabled={!examLevel || !examSeries}
            onClick={() => {
              mutatePostCheckEligibility({
                examLevelUuid: examLevel || '',
                examSeriesUuid: examSeries || '',
              });
              setDialogCustomStatus('loading');
            }}
            type="submit"
            status={dialogCustomStatus}
          >
            {t('specialConsideration->>SelectExamLevelModal.Proceed')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
export default SelectExamLevelModal;
